<template>
  <div class="ideas-page">
    <div class="ideas-hero">
      <div>
        <div class="ideas-kicker">DES REPAS PERSONNALISÉS, À LA CARTE</div>
        <h1>Mes idées de repas personnalisés pour une journée</h1>
        <p class="ideas-hero__intro">Choisissez pour qui, le type de repas, vos envies et vos contraintes : MMM vous propose immédiatement quelques recettes adaptées à votre profil, sans avoir à générer toute une semaine de menus.</p>
      </div>
      <div v-if="selectionHistory.length" class="recent-selections">
        <button type="button" class="recent-main" @click="restoreHistory(0)">Voir ma dernière sélection</button>
        <button v-for="(item, index) in selectionHistory.slice(1, 3)" :key="item.savedAt" type="button" class="recent-secondary" @click="restoreHistory(index + 1)">
          Sélection du {{ formatSavedDate(item.savedAt) }}
        </button>
      </div>
    </div>

    <div class="ideas-layout">
      <section class="ideas-form-card">
        <div class="form-block">
          <h2>Pour qui ?</h2>
          <div class="choice-row">
            <button
              type="button"
              class="choice-pill"
              :class="{ active: allMembersSelected }"
              @click="selectAllMembers"
            >
              Toute la famille
            </button>
            <button
              v-for="member in members"
              :key="member.id"
              type="button"
              class="choice-pill"
              :class="{ active: form.consumer_ids.includes(member.id) }"
              @click="toggleMember(member.id)"
            >
              {{ member.name }}
            </button>
          </div>
        </div>

        <div class="form-block">
          <h2>Quels repas ?</h2>
          <p class="hint">Je peux en choisir un, deux ou les trois.</p>
          <div class="choice-grid">
            <label class="check-card" :class="{ active: form.meals.includes('breakfast') }">
              <input v-model="form.meals" type="checkbox" value="breakfast" />
              <strong>Petit déjeuner</strong>
              <span>30 % de l'énergie quotidienne</span>
            </label>
            <label class="check-card" :class="{ active: form.meals.includes('lunch') }">
              <input v-model="form.meals" type="checkbox" value="lunch" />
              <strong>Déjeuner</strong>
              <span>40 % de l'énergie quotidienne</span>
            </label>
            <label class="check-card" :class="{ active: form.meals.includes('dinner') }">
              <input v-model="form.meals" type="checkbox" value="dinner" />
              <strong>Dîner</strong>
              <span>25 % de l'énergie quotidienne</span>
            </label>
          </div>
          <div class="meal-counts">
            <div v-for="meal in form.meals" :key="`count-${meal}`" class="meal-count">
              <span>{{ mealLabel(meal) }} : combien d’idées ?</span>
              <div class="count-buttons">
                <button
                  v-for="count in [1, 2, 3, 4]"
                  :key="`${meal}-${count}`"
                  type="button"
                  :class="{ active: form.meal_counts[meal] === count }"
                  @click="form.meal_counts[meal] = count"
                >{{ count }}</button>
              </div>
            </div>
          </div>
        </div>

        <div class="form-block">
          <h2>Je pars de quoi ?</h2>
          <p class="hint">
            Facultatif : j'indique ce que j'ai déjà ou ce que j'ai envie de manger.
          </p>
          <input
            v-model.trim="form.ingredients"
            class="ingredient-input"
            type="text"
            placeholder="Ex. poulet courgettes riz"
          />
          <small>Les virgules sont facultatives : espaces, virgules ou « et » sont acceptés. Si vous indiquez plusieurs aliments, ils devront tous être présents dans la recette.</small>
        </div>

        <div class="form-block">
          <h2>Temps de préparation maximum</h2>
          <p class="hint">Comme je ne prépare qu'un repas, je peux aussi chercher des recettes très rapides.</p>
          <div class="choice-row">
            <button
              v-for="option in preparationTimes"
              :key="String(option.value)"
              type="button"
              class="choice-pill"
              :class="{ active: form.max_preparation_time === option.value }"
              @click="form.max_preparation_time = option.value"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <div class="form-block">
          <h2>Orientation du repas</h2>
          <div class="radio-grid">
            <label v-for="option in availableOrientations" :key="option.value" class="radio-card" :class="{ active: form.orientation === option.value }">
              <input v-model="form.orientation" type="radio" :value="option.value" />
              <span class="orientation-title">
                <strong>{{ option.label }}</strong>
                <span
                  class="orientation-help"
                  :data-help="option.help"
                  :aria-label="option.help"
                  tabindex="0"
                >?</span>
              </span>
            </label>
          </div>
        
          <div v-if="weightLossActive" class="weight-loss-note">
            Objectif de poids pris en compte : MMM privilégie des repas P-G ou P-L et ne propose pas de P-L-G. Chez l’adulte, l’objectif énergétique est réduit ; chez un mineur, MMM n’applique pas de déficit calorique automatique.
          </div>
        </div>

        <div class="form-block">
          <h2 class="protein-heading">
            Protéines
            <span
              class="orientation-help"
              tabindex="0"
              aria-label="Information sur l’adaptation des protéines"
              data-help="MMM adapte déjà l’objectif protéique à votre profil. Chez les sportifs et les personnes âgées, le mode Standard tient déjà compte de besoins protéiques plus élevés."
            >?</span>
          </h2>
          <div class="radio-grid two">
            <label class="radio-card" :class="{ active: form.protein_mode === 'standard' }">
              <input v-model="form.protein_mode" type="radio" value="standard" />
              <strong>Standard</strong>
              <span>Objectif protéique physiologique adapté à mon profil.</span>
            </label>
            <label class="radio-card" :class="{ active: form.protein_mode === 'max' }">
              <input v-model="form.protein_mode" type="radio" value="max" />
              <strong>Maximum raisonnable</strong>
              <span>Jusqu’à +30 % par rapport à mon objectif, en augmentant uniquement les sources protéiques du repas.</span>
            </label>
          </div>
        </div>

        <div class="form-block">
          <h2 class="protein-heading">
            Un de ces repas est-il lié à une séance de sport ?
            <span
              class="orientation-help"
              tabindex="0"
              aria-label="Information sur l’adaptation sportive"
              data-help="Avant une séance, MMM augmente surtout la part glucidique et l’énergie du repas selon la durée et l’intensité, avec un repas conseillé idéalement environ 2 h avant l’effort. Après une séance, MMM privilégie la recharge en glucides, notamment via des féculents de qualité et des légumineuses quand ils sont adaptés, tout en conservant l’objectif protéique déjà personnalisé au profil."
            >?</span>
          </h2>

          <div class="binary-sport-choice">
            <label :class="{ active: form.sport_enabled === false }">
              <input v-model="form.sport_enabled" type="radio" :value="false" />
              Non
            </label>
            <label :class="{ active: form.sport_enabled === true }">
              <input v-model="form.sport_enabled" type="radio" :value="true" />
              Oui
            </label>
          </div>

          <p v-if="form.sport_enabled" class="hint sport-table-hint">
            Précisez uniquement les repas concernés. La durée correspond à la durée de la séance.
          </p>

          <div v-if="form.sport_enabled" class="sport-table">
            <div class="sport-table-head">
              <div>Repas</div>
              <div>Contexte</div>
              <div>Durée de la séance</div>
              <div>Intensité</div>
            </div>

            <div v-for="meal in form.meals" :key="'sport-' + meal" class="sport-table-row">
              <div class="sport-meal-name">{{ mealLabel(meal) }}</div>

              <div>
                <select v-model="form.sport_by_meal[meal].context" class="sport-select">
                  <option value="standard">Repas habituel</option>
                  <option value="before">Avant une séance</option>
                  <option value="after">Après une séance</option>
                </select>
                <small v-if="form.sport_by_meal[meal].context === 'before'" class="sport-row-note">
                  Idéalement environ 2 h avant la séance.
                </small>
              </div>

              <div>
                <select
                  v-model="form.sport_by_meal[meal].duration"
                  class="sport-select"
                  :disabled="form.sport_by_meal[meal].context === 'standard'"
                >
                  <option value="short">Moins de 1 h</option>
                  <option value="medium">1 h à 1 h 30</option>
                  <option value="long">Plus de 1 h 30</option>
                </select>
              </div>

              <div>
                <select
                  v-model="form.sport_by_meal[meal].intensity"
                  class="sport-select"
                  :disabled="form.sport_by_meal[meal].context === 'standard'"
                >
                  <option value="moderate">Modérée</option>
                  <option value="intermediate">Intermédiaire</option>
                  <option value="high">Élevée</option>
                </select>
              </div>
            </div>
          </div>

          <div
            v-if="form.sport_enabled && selectedMembers.some(member => member.want_to_loose_weight) && form.meals.some(meal => form.sport_by_meal[meal]?.context === 'after')"
            class="weight-loss-note"
          >
            Objectif perte de poids : MMM adapte la récupération sans augmenter inutilement l’apport énergétique.
          </div>
        </div>

        <button class="generate-button" type="button" :disabled="loading || !canSubmit" @click="generateIdeas">
          {{ loading ? "MMM cherche les meilleures recettes…" : "Me proposer des recettes" }}
        </button>
        <p v-if="error" class="error-message">{{ error }}</p>
      </section>

      <section v-if="hasResults" class="ideas-results">
        <div class="results-heading">
          <div class="ideas-kicker">MES PROPOSITIONS</div>
          <h2>Des recettes adaptées à {{ response.member.name }}</h2>
          <p>{{ response.family ? `Les quantités ci-dessous sont calculées pour ${response.member.name}.` : "Les quantités ci-dessous sont calculées pour la personne sélectionnée." }}</p>
        </div>

        <div v-for="meal in form.meals" :key="meal" class="meal-results">
          <h3>{{ mealLabel(meal) }}</h3>
          <p v-if="!response.results[meal] || !response.results[meal].length" class="empty-result">
            Aucune recette suffisamment compatible trouvée. Essaie avec moins d'ingrédients imposés.
          </p>

          <article v-for="recipe in visibleRecipes(meal)" :key="meal + '-' + recipe.id" class="recipe-card">
            <div class="recipe-head">
              <div class="recipe-image-wrap">
                <img v-if="recipe.photo" :src="mediaSrc(recipe.photo)" :alt="recipe.name" />
                <div v-else class="recipe-image-placeholder">MMM</div>
              </div>

              <div class="recipe-summary">
                <div class="recipe-summary-top">
                  <div class="recipe-tags">
                    <span>{{ recipe.macro_nutriments }}</span>
                    <span>{{ recipe.preparation_time }} min</span>
                  </div>
                  <button
                    type="button"
                    class="favorite-button"
                    :class="{ active: recipe.liked }"
                    :title="recipe.liked ? 'Retirer de mes favoris' : 'Ajouter à mes favoris'"
                    @click="toggleFavorite(recipe)"
                  >
                    ♥
                  </button>
                </div>

                <h4>{{ recipe.name }}</h4>
                <div v-if="meal !== 'breakfast' && recipeMealimScore(recipe) != null" class="idea-mealim-score">
                  <span>Score MEALIM</span>
                  <strong>{{ recipeMealimScore(recipe) }}/100</strong>
                </div>
                <MealimAnalysisDetails
                  v-if="meal !== 'breakfast' && recipeMealimScore(recipe) != null && (recipe.id != null || (recipe.dish && recipe.dish.id != null))"
                  :dish-id="recipe.id || (recipe.dish && recipe.dish.id)"
                />

                <div class="recipe-nutrition">
                  <div>
                    <strong>{{ recipe.personalization.estimated_energy_kcal }}</strong>
                    <span>kcal</span>
                  </div>
                  <div>
                    <strong>{{ String(recipe.personalization.estimated_protein_g).replace('.', ',') }}</strong>
                    <span>g protéines</span>
                  </div>
                  <div>
                    <strong v-if="!response.family">Adaptées</strong>
                    <strong v-else>{{ recipe.personalization.family_size }}</strong>
                    <span>{{ response.family ? "personnes" : "quantités" }}</span>
                  </div>
                </div>

                <div class="recipe-context">
                  <span>Objectif : {{ recipe.personalization.energy_target_kcal }} kcal</span>
                  <span v-if="form.protein_mode === 'max'">Protéines renforcées</span>
                  <span v-if="recipe.personalization.sport_context === 'before'">Avant séance</span>
                  <span v-if="recipe.personalization.sport_context === 'after'">Après séance</span>
                </div>

                <div class="recipe-actions">
                  <button type="button" class="select-recipe-button" :class="{ active: isSelected(meal, recipe.id) }" @click="toggleSelection(meal, recipe)">
                    {{ isSelected(meal, recipe.id) ? "✓ Plat sélectionné" : "Sélectionner ce plat" }}
                  </button>
                  <button type="button" class="details-button" @click="toggleDetails(meal, recipe.id)">
                    {{ isDetailsOpen(meal, recipe.id) ? "Masquer la recette" : "Voir la recette" }}
                  </button>
                </div>
              </div>
            </div>

            <div v-if="isDetailsOpen(meal, recipe.id)" class="recipe-content">
              <section class="recipe-panel quantities-panel">
                <h5>{{ response.family ? `Quantités pour ${response.member.name}` : "Mes quantités" }}</h5>
                <ul class="ingredient-list">
                  <li v-for="ingredient in recipe.personalized_ingredients" :key="ingredient.name + ingredient.unit">
                    <span>{{ ingredient.name }}</span>
                    <strong>{{ ingredient.quantity }} {{ unitLabel(ingredient.unit) }}</strong>
                  </li>
                </ul>
              </section>

              <section class="recipe-panel recipe-preparation">
                <h5>Préparation</h5>
                <p>{{ recipe.recipe || "La préparation détaillée sera ajoutée à cette recette." }}</p>
              </section>
            </div>
          </article>
          <div v-if="response.results[meal] && response.results[meal].length" class="more-ideas-row">
            <button type="button" class="more-ideas-button" :disabled="loadingMore === meal" @click="loadMore(meal)">
              {{ loadingMore === meal ? "Recherche…" : `Ces plats ne me plaisent pas · ${form.meal_counts[meal]} autres idées` }}
            </button>
          </div>
        </div>

        <div class="selection-actions">
          <button type="button" class="favorites-choice-button" @click="openFavoritesForSelection">♥ Ajouter un de mes plats favoris</button>
          <button v-if="selectedCount && !selectionFinalized" type="button" class="finalize-selection-button" @click="finalizeSelection">
            Garder ma sélection ({{ selectedCount }})
          </button>
          <button v-if="selectionFinalized && selectedCount" type="button" class="shopping-selection-button" @click="openSelectionShoppingList">
            Faire la liste de courses
          </button>
          <button v-if="selectionFinalized" type="button" class="secondary-selection-button" @click="selectionFinalized = false">Modifier ma sélection</button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { api } from "@/api";
import MealimAnalysisDetails from "@/components/MealimAnalysisDetails.vue";
import { userState } from "@/utils/store";

export default {
  name: "MealIdeasView",
  components: { MealimAnalysisDetails },
  data() {
    return {
      loading: false,
      loadingMore: null,
      error: "",
      response: null,
      detailsOpen: {},
      selectedRecipes: { breakfast: [], lunch: [], dinner: [] },
      selectionFinalized: false,
      selectionHistory: [],
      form: {
        consumer_id: null,
        consumer_ids: [],
        meals: ["lunch", "dinner"],
        ingredients: "",
        max_preparation_time: null,
        orientation: "balanced",
        protein_mode: "standard",
        sport_enabled: false,
        sport_by_meal: {
          breakfast: { context: "standard", duration: "short", intensity: "moderate" },
          lunch: { context: "standard", duration: "short", intensity: "moderate" },
          dinner: { context: "standard", duration: "short", intensity: "moderate" },
        },
        meal_counts: { breakfast: 3, lunch: 3, dinner: 3 },
      },
      preparationTimes: [
        { value: null, label: "Peu importe" },
        { value: 20, label: "Moins de 20 min" },
        { value: 30, label: "Moins de 30 min" },
        { value: 40, label: "Moins de 40 min" },
      ],
      orientations: [
        {
          value: "balanced",
          label: "Standard",
          help: "MMM choisit librement la structure du repas la plus adaptée à votre profil et au contexte : P-G, P-L ou P-L-G.",
        },
        {
          value: "carb",
          label: "Plutôt glucidique",
          help: "MMM privilégie les repas comportant une part plus importante de glucides.",
        },
        {
          value: "fat",
          label: "Plutôt lipidique",
          help: "MMM privilégie les repas comportant une part plus importante de lipides.",
        },
        {
          value: "carb_fat",
          label: "Glucido-lipidique",
          help: "MMM privilégie les repas associant davantage glucides et lipides.",
        },
      ],
    };
  },
  computed: {
    members() {
      return userState.me?.consumer_account?.consumers || [];
    },
    selectedMembers() {
      const ids = Array.isArray(this.form.consumer_ids) ? this.form.consumer_ids : [];
      return this.members.filter((member) => ids.includes(member.id));
    },
    selectedMember() {
      return this.selectedMembers.length === 1 ? this.selectedMembers[0] : null;
    },
    allMembersSelected() {
      return this.members.length > 0 && this.selectedMembers.length === this.members.length;
    },
    weightLossActive() {
      return this.selectedMembers.some(member => member.want_to_loose_weight);
    },
    availableOrientations() {
      return this.weightLossActive
        ? this.orientations.filter(option => option.value !== "carb_fat")
        : this.orientations;
    },
    canSubmit() {
      return this.selectedMembers.length > 0 && Array.isArray(this.form.meals) && this.form.meals.length > 0;
    },
    hasResults() {
      return !!this.response?.results;
    },
    selectedCount() {
      return Object.values(this.selectedRecipes || {}).reduce(
        (sum, recipes) => sum + (Array.isArray(recipes) ? recipes.length : 0),
        0
      );
    },
  },
  mounted() {
    this.loadSelectionHistory();
    this.restoreIdeaSession();
    this.ensureSportConfigs();
    this.importFavoriteSelection();
    this.$nextTick(() => this.hydrateMealimScores());
    if (this.members.length && !this.form.consumer_ids.length) {
      this.form.consumer_ids = this.members.map(member => member.id);
      this.form.consumer_id = null;
    }
  },
  watch: {
    members: {
      immediate: true,
      handler(value) {
        if ((!Array.isArray(this.form.consumer_ids) || !this.form.consumer_ids.length) && value && value.length) {
          this.form.consumer_ids = value.map(member => member.id);
          this.form.consumer_id = null;
        }
      },
    },
    "form.meals": {
      deep: true,
      handler() {
        this.ensureSportConfigs();
      },
    },
    weightLossActive(value) {
      if (value && this.form.orientation === "carb_fat") {
        this.form.orientation = "balanced";
      }
    },
  },
  methods: {
    normalizeSportConfig(value, legacyContext = "standard") {
      const source = value && typeof value === "object" ? value : {};
      const context = ["standard", "before", "after"].includes(source.context)
        ? source.context
        : (["standard", "before", "after"].includes(legacyContext) ? legacyContext : "standard");
      const duration = ["short", "medium", "long"].includes(source.duration) ? source.duration : "short";
      const intensity = ["moderate", "intermediate", "high"].includes(source.intensity) ? source.intensity : "moderate";
      return { context, duration, intensity };
    },
    ensureSportConfigs() {
      const meals = ["breakfast", "lunch", "dinner"];
      meals.forEach((meal) => {
        if (!this.form.sport_by_meal) this.$set(this.form, "sport_by_meal", {});
        if (!this.form.sport_by_meal[meal]) {
          this.$set(this.form.sport_by_meal, meal, this.normalizeSportConfig(null));
        }
      });
    },
    selectAllMembers() {
      this.form.consumer_ids = this.members.map(member => member.id);
      this.form.consumer_id = null;
    },
    toggleMember(memberId) {
      const current = Array.isArray(this.form.consumer_ids) ? this.form.consumer_ids : [];
      this.form.consumer_ids = current.includes(memberId)
        ? current.filter(id => id !== memberId)
        : [...current, memberId];
      this.form.consumer_id = null;
    },
    normalizeRecipe(recipe, meal) {
      if (!recipe || typeof recipe !== "object") return null;
      const personalization = recipe.personalization && typeof recipe.personalization === "object"
        ? recipe.personalization
        : {};
      return {
        ...recipe,
        mealim_score: recipe.mealim_score ?? recipe.dish?.mealim_score ?? null,
        personalization: {
          estimated_energy_kcal: 0,
          estimated_protein_g: 0,
          quantity_factor: 1,
          family_size: 1,
          energy_target_kcal: 0,
          ...personalization,
          meal: personalization.meal || meal,
        },
        personalized_ingredients: Array.isArray(recipe.personalized_ingredients)
          ? recipe.personalized_ingredients
          : [],
      };
    },
    normalizeRecipeList(list, meal) {
      if (!Array.isArray(list)) return [];
      return list.map(recipe => this.normalizeRecipe(recipe, meal)).filter(Boolean);
    },
    normalizeSelection(value) {
      const source = value && typeof value === "object" ? value : {};
      return {
        breakfast: this.normalizeRecipeList(source.breakfast, "breakfast"),
        lunch: this.normalizeRecipeList(source.lunch, "lunch"),
        dinner: this.normalizeRecipeList(source.dinner, "dinner"),
      };
    },
    normalizeForm(value) {
      const source = value && typeof value === "object" ? value : {};
      const allowedMeals = ["breakfast", "lunch", "dinner"];
      let consumerIds = Array.isArray(source.consumer_ids)
        ? source.consumer_ids.map(Number).filter(id => this.members.some(member => member.id === id))
        : [];
      if (!consumerIds.length && source.consumer_id === "family") {
        consumerIds = this.members.map(member => member.id);
      } else if (!consumerIds.length && source.consumer_id != null && source.consumer_id !== "family") {
        const legacyId = Number(source.consumer_id);
        if (this.members.some(member => member.id === legacyId)) consumerIds = [legacyId];
      }
      return {
        ...this.form,
        ...source,
        consumer_id: null,
        consumer_ids: consumerIds,
        meals: Array.isArray(source.meals)
          ? source.meals.filter(meal => allowedMeals.includes(meal))
          : this.form.meals,
        meal_counts: {
          ...this.form.meal_counts,
          ...(source.meal_counts && typeof source.meal_counts === "object" ? source.meal_counts : {}),
        },
        sport_enabled: source.sport_enabled === true,
        sport_by_meal: {
          breakfast: this.normalizeSportConfig(source.sport_by_meal?.breakfast, source.sport_context),
          lunch: this.normalizeSportConfig(source.sport_by_meal?.lunch, source.sport_context),
          dinner: this.normalizeSportConfig(source.sport_by_meal?.dinner, source.sport_context),
        },
      };
    },
    normalizeResponse(value) {
      if (!value || typeof value !== "object" || !value.results) return null;
      const results = value.results && typeof value.results === "object" ? value.results : {};
      return {
        ...value,
        member: value.member && typeof value.member === "object"
          ? value.member
          : { name: value.family ? "Toute la famille" : "Ma sélection" },
        results: {
          breakfast: this.normalizeRecipeList(results.breakfast, "breakfast"),
          lunch: this.normalizeRecipeList(results.lunch, "lunch"),
          dinner: this.normalizeRecipeList(results.dinner, "dinner"),
        },
      };
    },
    visibleRecipes(meal) {
      const recipes = this.response?.results?.[meal] || [];
      if (!this.selectionFinalized) return recipes;
      const ids = new Set((this.selectedRecipes[meal] || []).map(recipe => recipe.id));
      return recipes.filter(recipe => ids.has(recipe.id));
    },
    isSelected(meal, id) {
      return (this.selectedRecipes[meal] || []).some(recipe => recipe.id === id);
    },
    toggleSelection(meal, recipe) {
      const current = this.selectedRecipes[meal] || [];
      this.$set(this.selectedRecipes, meal, this.isSelected(meal, recipe.id)
        ? current.filter(item => item.id !== recipe.id)
        : [...current, recipe]);
      this.selectionFinalized = false;
      this.persistIdeaSession();
    },
    finalizeSelection() {
      if (!this.selectedCount) return;
      this.selectionFinalized = true;
      this.persistIdeaSession();
      this.archiveSelection();
      this.$nextTick(() => {
        this.hydrateMealimScores();
        this.$el.querySelector(".ideas-results")?.scrollIntoView({ behavior: "smooth" });
      });
    },
    persistIdeaSession() {
      localStorage.setItem("mmm-meal-ideas-session", JSON.stringify({
        form: this.form,
        response: this.response,
        selectedRecipes: this.selectedRecipes,
        selectionFinalized: this.selectionFinalized,
      }));
    },
    async hydrateMealimScores() {
      // Older browser-saved selections may not contain the MEALIM score yet.
      // Fetch the catalogue score for those main dishes.
      const collections = [
        this.response?.results,
        this.selectedRecipes,
        ...this.selectionHistory.map(item => item.selectedRecipes),
      ].filter(Boolean);
      const recipes = collections.flatMap(collection =>
        Object.entries(collection).flatMap(([meal, list]) =>
          meal === "breakfast" || !Array.isArray(list)
            ? []
            : list.filter(recipe =>
                recipe && this.recipeMealimScore(recipe) == null && recipe.id != null
              )
        )
      );
      const ids = [...new Set(recipes.map(recipe => String(recipe.id)))];
      if (!ids.length) return;

      const scores = new Map();
      await Promise.all(ids.map(async id => {
        try {
          const response = await api.get(`/recipes/${id}/`);
          const recipeData = response.data?.recipe || response.data;
          if (recipeData?.mealim_score != null) {
            scores.set(id, recipeData.mealim_score);
          }
        } catch (error) {
          // Keep the recipe visible without a score if it is unavailable.
        }
      }));
      if (!scores.size) return;

      for (const collection of collections) {
        for (const recipe of Object.values(collection).flat()) {
          const score = scores.get(String(recipe.id));
          if (score != null) this.$set(recipe, "mealim_score", score);
        }
      }
      this.persistIdeaSession();
      localStorage.setItem("mmm-meal-ideas-history", JSON.stringify(this.selectionHistory));
    },
    restoreIdeaSession() {
      try {
        const saved = JSON.parse(localStorage.getItem("mmm-meal-ideas-session") || "null");
        if (!saved || typeof saved !== "object") return;
        if (saved.form) this.form = this.normalizeForm(saved.form);
        const restoredResponse = this.normalizeResponse(saved.response);
        if (restoredResponse) this.response = restoredResponse;
        this.selectedRecipes = this.normalizeSelection(saved.selectedRecipes);
        this.selectionFinalized = !!saved.selectionFinalized && this.selectedCount > 0;
        this.persistIdeaSession();
      } catch (e) {
        this.response = null;
        this.selectedRecipes = { breakfast: [], lunch: [], dinner: [] };
        this.selectionFinalized = false;
      }
    },
    loadSelectionHistory() {
      try {
        const history = JSON.parse(localStorage.getItem("mmm-meal-ideas-history") || "[]");
        this.selectionHistory = (Array.isArray(history) ? history : [])
          .filter(item => item && typeof item === "object")
          .map(item => ({
            ...item,
            selectedRecipes: this.normalizeSelection(item.selectedRecipes),
          }))
          .filter(item => Object.values(item.selectedRecipes).some(list => list.length))
          .slice(0, 3);
      } catch (e) {
        this.selectionHistory = [];
      }
    },
    archiveSelection() {
      const snapshot = {
        savedAt: new Date().toISOString(),
        form: JSON.parse(JSON.stringify(this.form)),
        member: this.response?.member || null,
        family: !!this.response?.family,
        selectedRecipes: JSON.parse(JSON.stringify(this.selectedRecipes)),
      };
      const signature = JSON.stringify(Object.values(snapshot.selectedRecipes).flat().map(r => r.id).sort());
      const previous = this.selectionHistory.filter(item => {
        const oldSignature = JSON.stringify(Object.values(item.selectedRecipes || {}).flat().map(r => r.id).sort());
        return oldSignature !== signature;
      });
      this.selectionHistory = [snapshot, ...previous].slice(0, 3);
      localStorage.setItem("mmm-meal-ideas-history", JSON.stringify(this.selectionHistory));
    },
    restoreHistory(index) {
      const item = this.selectionHistory[index];
      if (!item) return;
      this.form = this.normalizeForm(item.form);
      this.selectedRecipes = this.normalizeSelection(item.selectedRecipes);
      this.response = {
        member: item.member || { name: item.family ? "Toute la famille" : "Ma sélection" },
        family: !!item.family,
        results: JSON.parse(JSON.stringify(this.selectedRecipes)),
      };
      this.selectionFinalized = true;
      this.persistIdeaSession();
      this.$nextTick(() => this.$el.querySelector(".ideas-results")?.scrollIntoView({ behavior: "smooth" }));
    },
    formatSavedDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "date inconnue";
      return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(date);
    },
    openFavoritesForSelection() {
      this.persistIdeaSession();
      this.$router.push({ name: "my-liked-recipes", query: { selectForIdeas: "1" } });
    },
    importFavoriteSelection() {
      try {
        const added = JSON.parse(localStorage.getItem("mmm-meal-ideas-favorite") || "null");
        if (!added || !added.recipe || !added.meal) return;
        localStorage.removeItem("mmm-meal-ideas-favorite");
        const meal = added.meal;
        const recipe = added.recipe;
        const family = !!this.response?.family;
        const reference = Object.values(this.response?.results || {}).flat()[0]?.personalization;
        const factor = reference?.quantity_factor || 1;
        const ingredientScale = factor * (family ? (reference?.family_size || 1) : 1);
        recipe.personalized_ingredients = (recipe.dish_lines || []).map(line => ({
          name: line.aliment?.display_name || line.aliment?.name || "Aliment",
          quantity: Math.round((line.quantity || 0) * ingredientScale * 10) / 10,
          unit: line.unit,
        }));
        recipe.personalization = {
          ...(reference || {}),
          member: this.response?.member?.name || "",
          meal,
          quantity_factor: factor,
          family,
          family_size: reference?.family_size || 1,
          estimated_energy_kcal: Math.round((recipe.nutrition_per_person?.energy || 0) * factor),
          estimated_protein_g: Math.round((recipe.nutrition_per_person?.protein || 0) * factor * 10) / 10,
          energy_target_kcal: reference?.energy_target_kcal || 0,
        };
        if (!this.response) return;
        if (!this.response.results[meal]) this.$set(this.response.results, meal, []);
        if (!this.response.results[meal].some(item => item.id === recipe.id)) this.response.results[meal].push(recipe);
        if (!this.form.meals.includes(meal)) this.form.meals.push(meal);
        if (!Array.isArray(this.selectedRecipes[meal])) this.$set(this.selectedRecipes, meal, []);
        if (!this.isSelected(meal, recipe.id)) this.selectedRecipes[meal].push(recipe);
        this.selectionFinalized = false;
        this.persistIdeaSession();
      } catch (e) { /* favori ignoré si incomplet */ }
    },
    openSelectionShoppingList() {
      this.persistIdeaSession();
      localStorage.setItem("mmm-meal-ideas-shopping", JSON.stringify(this.selectedRecipes));
      this.$router.push({ name: "meal-ideas-shopping" });
    },
    async generateIdeas() {
      if (!this.canSubmit) return;
      this.loading = true;
      this.error = "";
      try {
        const payload = {
          ...this.form,
          sport_by_meal: this.form.sport_enabled
            ? this.form.sport_by_meal
            : {
                breakfast: { context: "standard", duration: "short", intensity: "moderate" },
                lunch: { context: "standard", duration: "short", intensity: "moderate" },
                dinner: { context: "standard", duration: "short", intensity: "moderate" },
              },
        };
        const response = await api.post("/meal-ideas/", payload);
        this.response = this.normalizeResponse(response.data);
        await this.hydrateMealimScores();
        this.selectionFinalized = false;
        this.selectedRecipes = { breakfast: [], lunch: [], dinner: [] };
        this.persistIdeaSession();
        this.$nextTick(() => {
          const el = this.$el.querySelector(".ideas-results");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      } catch (error) {
        this.error = error.response?.data?.detail || "Impossible de générer les idées pour le moment.";
      } finally {
        this.loading = false;
      }
    },
    mediaSrc(uri) {
      if (!uri) return "";
      if (/^https?:\/\//i.test(uri)) return uri;
      return `${process.env.VUE_APP_BASE_URL}${uri}`;
    },
    recipeMealimScore(recipe) {
      return recipe?.mealim_score ?? recipe?.dish?.mealim_score ?? null;
    },
    detailKey(meal, id) {
      return `${meal}-${id}`;
    },
    isDetailsOpen(meal, id) {
      return !!this.detailsOpen[this.detailKey(meal, id)];
    },
    toggleDetails(meal, id) {
      const key = this.detailKey(meal, id);
      this.$set(this.detailsOpen, key, !this.detailsOpen[key]);
    },
    async toggleFavorite(recipe) {
      try {
        const response = await api.post(`/recipes/${recipe.id}/toggle_like/`);
        recipe.liked = response.data?.liked ?? !recipe.liked;
      } catch (error) {
        this.error = "Impossible d'enregistrer ce favori pour le moment.";
      }
    },
    async loadMore(meal) {
      if (this.loadingMore) return;
      this.loadingMore = meal;
      this.error = "";
      try {
        const currentIds = Object.values(this.response?.results || {})
          .flat()
          .map((recipe) => recipe.id);
        const payload = {
          ...this.form,
          meals: [meal],
          sport_by_meal: this.form.sport_enabled
            ? this.form.sport_by_meal
            : {
                breakfast: { context: "standard", duration: "short", intensity: "moderate" },
                lunch: { context: "standard", duration: "short", intensity: "moderate" },
                dinner: { context: "standard", duration: "short", intensity: "moderate" },
              },
          exclude_ids: currentIds,
        };
        const response = await api.post("/meal-ideas/", payload);
        const newRecipes = this.normalizeRecipeList(response.data?.results?.[meal], meal);
        this.$set(this.response.results, meal, newRecipes);
        await this.hydrateMealimScores();
        this.detailsOpen = {};
      } catch (error) {
        this.error = "Impossible de proposer d'autres recettes pour le moment.";
      } finally {
        this.loadingMore = null;
      }
    },
    mealLabel(meal) {
      return {
        breakfast: "Petit déjeuner",
        lunch: "Déjeuner",
        dinner: "Dîner",
      }[meal] || meal;
    },
    unitLabel(unit) {
      return {
        piece: "pièce",
        pincee: "pincée",
      }[unit] || unit;
    },
  },
};
</script>

<style scoped>
.ideas-page {
  width: 100%;
  padding: 18px 34px 70px;
  background: #f6f7f1;
  min-height: 100vh;
  box-sizing: border-box;
}
.ideas-hero, .ideas-form-card, .ideas-results {
  max-width: 1120px;
  margin: 0 auto;
}
.ideas-hero {
  margin-bottom: 10px;
}
.recent-selections { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px; }
.recent-selections button { border-radius: 999px; padding: 8px 13px; cursor: pointer; font: inherit; font-weight: 700; }
.recent-main { border: 1px solid #77af24; background: #edf6df; color: #395d0d; }
.recent-secondary { border: 1px solid #d9dfd1; background: #fff; color: #52604b; }
.ideas-kicker {
  color: #6d9f25;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: .08em;
}
.ideas-hero h1, .results-heading h2 {
  color: #1d2c13;
  margin: 4px 0 2px;
}
.ideas-hero p, .results-heading p, .hint, .form-block small {
  color: #65705f;
}
.ideas-hero__intro {
  max-width: 900px;
  margin: 7px 0 0;
  font-size: 14px;
  line-height: 1.45;
}
.ideas-form-card {
  background: white;
  border-radius: 22px;
  padding: 20px 24px;
  box-shadow: 0 8px 30px rgba(38, 55, 22, .08);
}
.form-block + .form-block {
  border-top: 1px solid #edf0e8;
  margin-top: 14px;
  padding-top: 14px;
}
.form-block h2 {
  font-size: 20px;
  margin: 0 0 4px;
  color: #26351b;
}
.hint {
  margin: 0 0 8px;
  font-size: 13px;
}
.choice-row {
  display: flex;
  gap: 9px;
  flex-wrap: wrap;
}
.choice-pill {
  border: 1px solid #d9dfd1;
  background: #fff;
  border-radius: 999px;
  padding: 9px 16px;
  cursor: pointer;
  font: inherit;
}
.choice-pill.active {
  border-color: #77af24;
  background: #edf6df;
  color: #395d0d;
  font-weight: 700;
}
.meal-counts {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
}
.meal-count {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #52604b;
  font-size: 13px;
  font-weight: 700;
}
.count-buttons {
  display: flex;
  gap: 4px;
}
.count-buttons button {
  width: 30px;
  height: 30px;
  border: 1px solid #d9dfd1;
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
}
.count-buttons button.active {
  border-color: #77af24;
  background: #edf6df;
  color: #395d0d;
}
.protein-context-note {
  margin: 0 0 12px;
  color: #65705f;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}
.protein-heading {
  display: flex;
  align-items: center;
  gap: 8px;
}
.orientation-title {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}
.orientation-help {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 19px;
  height: 19px;
  border: 1px solid #77af24;
  border-radius: 50%;
  color: #557f1b;
  background: #f4f9ec;
  font-size: 12px;
  font-weight: 800;
  cursor: help;
  flex: 0 0 19px;
  outline: none;
}
.orientation-help:hover::after,
.orientation-help:focus::after {
  content: attr(data-help);
  position: absolute;
  left: 50%;
  bottom: calc(100% + 9px);
  transform: translateX(-50%);
  z-index: 50;
  width: min(280px, 70vw);
  padding: 9px 11px;
  border-radius: 9px;
  background: #26351b;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.35;
  text-align: left;
  box-shadow: 0 6px 18px rgba(0, 0, 0, .18);
}
.orientation-help:hover::before,
.orientation-help:focus::before {
  content: "";
  position: absolute;
  left: 50%;
  bottom: calc(100% + 3px);
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #26351b;
  z-index: 51;
}
.choice-grid, .radio-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.radio-grid {
  grid-template-columns: repeat(4, 1fr);
}
.radio-grid.two { grid-template-columns: repeat(2, 1fr); }
.radio-grid.three { grid-template-columns: repeat(3, 1fr); }
.check-card, .radio-card {
  border: 1px solid #dde3d6;
  border-radius: 14px;
  padding: 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 70px;
}
.check-card input, .radio-card input { position: absolute; opacity: 0; }
.check-card.active, .radio-card.active {
  border-color: #77af24;
  background: #f1f8e8;
  box-shadow: inset 0 0 0 1px #77af24;
}
.check-card span, .radio-card span {
  color: #73806d;
  font-size: 13px;
  line-height: 1.35;
}
.ingredient-input {
  width: 100%;
  border: 1px solid #d9dfd1;
  border-radius: 12px;
  padding: 13px 14px;
  font: inherit;
  box-sizing: border-box;
  margin-bottom: 6px;
}
.weight-loss-note {
  margin-top: 12px;
  background: #fff6dc;
  border-radius: 12px;
  padding: 12px 14px;
  color: #68531d;
  font-size: 14px;
}
.binary-sport-choice {
  display: flex;
  gap: 9px;
  margin: 10px 0 6px;
}
.binary-sport-choice label {
  min-width: 86px;
  padding: 10px 18px;
  border: 1px solid #d9dfd1;
  border-radius: 999px;
  background: white;
  cursor: pointer;
  font-weight: 800;
  color: #52604b;
}
.binary-sport-choice label.active {
  border-color: #77af24;
  background: #edf6df;
  color: #395d0d;
}
.binary-sport-choice input {
  margin-right: 6px;
}
.sport-table-hint {
  margin-top: 10px;
}
.sport-table {
  margin-top: 12px;
  border: 1px solid #dde3d6;
  border-radius: 14px;
  overflow: hidden;
}
.sport-table-head,
.sport-table-row {
  display: grid;
  grid-template-columns: 1fr 1.6fr 1.3fr 1.1fr;
  gap: 0;
  align-items: stretch;
}
.sport-table-head {
  background: #f1f6e9;
  color: #395d0d;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .04em;
}
.sport-table-head > div,
.sport-table-row > div {
  padding: 12px 13px;
  border-right: 1px solid #e2e8da;
}
.sport-table-head > div:last-child,
.sport-table-row > div:last-child {
  border-right: 0;
}
.sport-table-row {
  border-top: 1px solid #e8ece3;
  background: white;
}
.sport-meal-name {
  font-weight: 800;
  color: #26351b;
  display: flex;
  align-items: center;
}
.sport-select {
  width: 100%;
  min-height: 42px;
  border: 1px solid #d6decf;
  border-radius: 10px;
  padding: 8px 10px;
  background: white;
  font: inherit;
}
.sport-select:disabled {
  background: #f3f4f1;
  color: #9aa094;
}
.sport-row-note {
  display: block;
  margin-top: 6px;
  color: #66705f;
  font-size: 11px;
  line-height: 1.3;
}
.generate-button {
  width: 100%;
  margin-top: 18px;
  background: #77af24;
  color: white;
  border: 0;
  border-radius: 14px;
  padding: 15px 20px;
  font: inherit;
  font-weight: 800;
  cursor: pointer;
}
.generate-button:disabled { opacity: .6; cursor: default; }
.error-message { color: #a5382e; margin-bottom: 0; }
.ideas-results {
  margin-top: 32px;
}
.results-heading { margin-bottom: 20px; }
.meal-results { margin-top: 28px; }
.meal-results h3 {
  font-size: 24px;
  color: #26351b;
  margin-bottom: 12px;
}
.recipe-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 22px;
  border: 1px solid #e7ecdf;
  box-shadow: 0 8px 28px rgba(38, 55, 22, .07);
}
.recipe-head {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  min-height: 190px;
  border-bottom: 1px solid #edf0e8;
}
.recipe-image-wrap {
  min-height: 190px;
  background: #eef2e8;
}
.recipe-image-wrap img {
  width: 100%;
  height: 100%;
  min-height: 190px;
  object-fit: cover;
  display: block;
}
.recipe-image-placeholder {
  height: 100%;
  min-height: 190px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #77af24;
  font-size: 34px;
  font-weight: 800;
}
.recipe-summary {
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.recipe-summary-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.recipe-summary h4 {
  font-size: 25px;
  line-height: 1.2;
  margin: 9px 0 16px;
  color: #26351b;
}
.recipe-tags {
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
}
.recipe-tags span,
.recipe-context span {
  background: #edf4e3;
  color: #50731d;
  border-radius: 999px;
  padding: 5px 9px;
  font-size: 12px;
  font-weight: 700;
}
.idea-mealim-score {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 5px;
  margin: -8px 0 12px;
  padding: 5px 9px;
  border-radius: 999px;
  background: #f2f6eb;
  color: #587734;
  font-size: 12px;
}
.idea-mealim-score strong { font-size: 12px; }

.recipe-nutrition {
  display: grid;
  grid-template-columns: repeat(3, minmax(90px, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}
.recipe-nutrition > div {
  background: #f7f9f4;
  border-radius: 12px;
  padding: 11px 12px;
}
.recipe-nutrition strong {
  display: block;
  color: #26351b;
  font-size: 20px;
}
.recipe-nutrition span {
  display: block;
  margin-top: 2px;
  color: #75806f;
  font-size: 12px;
}
.recipe-context {
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
}
.favorite-button {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border: 1px solid #d9dfd1;
  border-radius: 50%;
  background: white;
  color: #a9afa3;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}
.favorite-button.active {
  color: #d84f58;
  border-color: #efb8bc;
  background: #fff5f5;
}
.recipe-actions {
  margin-top: 16px;
}
.details-button,
.more-ideas-button,
.favorites-shortcut button {
  border: 0;
  border-radius: 11px;
  padding: 11px 15px;
  font: inherit;
  font-weight: 800;
  cursor: pointer;
}
.details-button {
  background: #355a1c;
  color: white;
}
.more-ideas-row {
  display: flex;
  justify-content: center;
  margin: 14px 0 34px;
}
.more-ideas-button {
  background: white;
  color: #355a1c;
  border: 1px solid #cad8bb;
}
.more-ideas-button:disabled {
  opacity: .55;
}
.favorites-shortcut {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}
.favorites-shortcut button {
  background: #edf4e3;
  color: #355a1c;
}
.recipe-content {
  display: grid;
  grid-template-columns: minmax(280px, .9fr) 1.1fr;
  gap: 18px;
  padding: 20px;
  background: #fbfcf9;
}
.recipe-panel {
  background: white;
  border: 1px solid #edf0e8;
  border-radius: 14px;
  padding: 18px;
}
.recipe-panel h5 {
  margin: 0 0 12px;
  font-size: 17px;
  color: #26351b;
}
.ingredient-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.ingredient-list li {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
  align-items: baseline;
  padding: 8px 0;
  border-bottom: 1px solid #eef0eb;
}
.ingredient-list li:last-child {
  border-bottom: 0;
}
.ingredient-list span {
  min-width: 0;
  line-height: 1.35;
}
.ingredient-list strong {
  white-space: nowrap;
  color: #304716;
}
.recipe-preparation p {
  margin: 0;
  white-space: pre-line;
  line-height: 1.6;
  color: #4f594a;
}
.empty-result {
  background: white;
  padding: 18px;
  border-radius: 14px;
  color: #687362;
}
@media (max-width: 850px) {
  .ideas-page { padding: 72px 14px 50px; }
  .sport-table { border: 0; overflow: visible; }
  .sport-table-head { display: none; }
  .sport-table-row {
    grid-template-columns: 1fr;
    border: 1px solid #dde3d6;
    border-radius: 14px;
    margin-bottom: 10px;
    overflow: hidden;
  }
  .sport-table-row > div {
    border-right: 0;
    border-bottom: 1px solid #edf0e8;
  }
  .sport-table-row > div:last-child { border-bottom: 0; }
  .sport-meal-name { background: #f1f6e9; color: #395d0d; }

  .ideas-form-card { padding: 18px; border-radius: 16px; }
  .choice-grid, .radio-grid, .radio-grid.two, .radio-grid.three { grid-template-columns: 1fr; }
  .recipe-head { grid-template-columns: 1fr; }
  .recipe-image-wrap, .recipe-image-wrap img, .recipe-image-placeholder { min-height: 190px; height: 190px; }
  .recipe-summary { padding: 18px; }
  .recipe-summary h4 { font-size: 22px; }
  .recipe-nutrition { grid-template-columns: repeat(3, 1fr); }
  .recipe-content { grid-template-columns: 1fr; padding: 14px; gap: 14px; }
  .recipe-panel { padding: 15px; }
}

.select-recipe-button, .finalize-selection-button, .shopping-selection-button, .favorites-choice-button, .secondary-selection-button {
  border-radius: 12px; padding: 10px 14px; border: 1px solid #77af24; cursor: pointer; font: inherit; font-weight: 700;
}
.select-recipe-button { background: #fff; color: #50731d; }
.select-recipe-button.active, .finalize-selection-button, .shopping-selection-button { background: #77af24; color: #fff; }
.selection-actions { display: flex; gap: 10px; flex-wrap: wrap; margin: 24px 0; }
.favorites-choice-button, .secondary-selection-button { background: #fff; color: #50731d; }
</style>
